/**
 * The one library taxonomy (roadmap X2).
 *
 * `sounds.category` is a single text value and the ranked-search RPC filters on
 * exactly one category, so a library filter that must combine with search has
 * to be one DB category, not a union. The parent categories are therefore real
 * DB values. This module is the single source for
 *
 *  - which retired DB names fold into which parent (`CATEGORY_MERGES`),
 *  - how a parent is labelled on /sounds (`categoryLabel`),
 *  - which categories show as chips (`buildLibraryCategories`).
 *
 * Every parent already exists as a DB value, so nothing here depends on the
 * data change in scripts/ops/x2-taxonomy/ having run. Chips list only
 * categories with active sounds, so the list shrinks when that change is
 * applied, with no deploy in lockstep.
 */

import type { Category } from './supabase-types'

/**
 * Retired DB category name -> the parent it folds into. The first six are the
 * X2 data change. The rest are earlier retargets whose sounds already moved;
 * they are listed so every old name resolves in one hop.
 */
export const CATEGORY_MERGES: Readonly<Record<string, string>> = {
  'Internet Culture': 'Classic Memes',
  'Comedy Sound Effects': 'Sound Effects',
  'TV Shows': 'Movies',
  'Sci-Fi & Action Films': 'Movies',
  'Game Shows & Quiz TV': 'Movies',
  'Adult Animation': 'Movies',
  'International Memes': 'Classic Memes',
  Memes: 'Classic Memes',
  Viral: 'Classic Memes',
  Games: 'Modern Gaming',
  'Retro Gaming': 'Modern Gaming',
  'Mario & Nintendo': 'Modern Gaming',
  'FPS & Battle Royale': 'Modern Gaming',
  'Classic Arcade & Fighting': 'Modern Gaming',
  'Minecraft & Sandbox': 'Modern Gaming',
  'Mobile & Casual Games': 'Modern Gaming',
  'Soccer & Football': 'Sports',
  'Pro Wrestling & MMA': 'Sports',
  'SpongeBob & Nickelodeon': 'Cartoons & Animation',
  'Hip-Hop & R&B': 'Music',
  'Jingles & Ringtones': 'Music',
  'Discord & App Sounds': 'Retro Tech',
  'F1 & Motorsport': 'Vehicles & Transport',
}

/**
 * The `categories` table keeps a row for every retired name, and the data
 * change leaves it alone. Anything that lists that table for a visitor or an
 * API client drops those rows here, so a retired shelf never reappears.
 */
export function withoutRetiredCategories<T extends { name: string }>(rows: readonly T[]): T[] {
  return rows.filter(row => !Object.hasOwn(CATEGORY_MERGES, row.name))
}

/** The folds the X2 data change performs, with the reason for each. */
export const X2_MERGES: ReadonlyArray<{ from: string; to: string; reason: string }> = [
  { from: 'Internet Culture', to: 'Classic Memes', reason: 'One meme shelf: the two overlap and a visitor cannot tell them apart' },
  { from: 'Comedy Sound Effects', to: 'Sound Effects', reason: 'Farts, airhorns and stingers are sound effects; one effects shelf' },
  { from: 'TV Shows', to: 'Movies', reason: 'One film and TV shelf, shown as Movies & TV' },
  { from: 'Sci-Fi & Action Films', to: 'Movies', reason: 'Under 10 sounds; action films belong with Movies' },
  { from: 'Game Shows & Quiz TV', to: 'Movies', reason: 'Under 10 sounds; quiz TV belongs with Movies & TV' },
]

/**
 * The write-side half of the merge: ingest classifies into the older, finer
 * names, and without this the weekly batch would put "Internet Culture" rows
 * back the day after the data change runs.
 */
export function foldRetiredCategory(category: string): string {
  return CATEGORY_MERGES[category] ?? category
}

/** Chip labels that differ from the DB value. Everything else shows as stored. */
const LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  'Modern Gaming': 'Games',
  'Classic Memes': 'Memes',
  Movies: 'Movies & TV',
}

export function categoryLabel(dbName: string): string {
  return LABEL_OVERRIDES[dbName] ?? dbName
}

/**
 * The chip list for the library: only categories with active sounds, biggest
 * first. `counts` is keyed by DB name and already leaves out mature-flagged
 * rows, so a chip never promises more than its filter lists. A name missing
 * from the `categories` table still shows, built from its count.
 */
export function buildLibraryCategories(
  categories: readonly Category[],
  counts: Readonly<Record<string, number>>,
): Category[] {
  const byName = new Map(categories.map(category => [category.name, category]))
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .sort(([nameA, a], [nameB, b]) => b - a || categoryLabel(nameA).localeCompare(categoryLabel(nameB)))
    .map(([name]) => byName.get(name) ?? ({ id: name, name } as Category))
}
